<template>
  <div v-if="supplier" class="details-wrapper">
    <div class="details-header">
      <div>Supplier #{{ supplier.reference_number }}</div>
    </div>
    <div v-if="supplier.reference_number" class="details-body">
      <div class="content-panel-wrapper">
        <div v-scroll-spy data-scroll-spy-id="main-tab" class="content-panel">
          <section class="details-section">
            <SupplierDetailsForm :supplier="supplier" />
          </section>
          <section class="details-section">
            <h1>{{ $t('suppliers.labels.attachment') }}</h1>
            <div v-if="canViewAttachment && supplier && supplier.reference_number">
              <Attachment
                :reference-number="supplier.reference_number"
                owner="SUPPLIER"
                @attachment-success="refreshAttachments"
              />
              <AttachmentsList
                attachment-type="SUPPLIER"
                :attachments="supplierAttachments"
                :attachment-options="attachmentOptions"
                @delete-attachment="confirmAttachmentDelete"
                @refresh="refreshAttachments"
              />
            </div>
          </section>
          <section class="details-section">
            <h1>{{ $t('suppliers.labels.history') }}</h1>
          </section>
          <section class="details-section">
            <h1>{{ $t('suppliers.labels.contacts') }}</h1>
            <tol-table :table-data="supplierContacts" :columns="supplierContactsColumns">
              <template v-slot:tableHeader>
                <th>{{ $t('suppliers.contacts.referenceNumber') }}</th>
                <th-sortable
                  :direction="
                    currentSortFieldForContacts === 'contactName'
                      ? currentSortDirectionForContacts
                      : 'none'
                  "
                  field-name="contactName"
                  @change="onContactsSortOrderChange"
                  >{{ $t('suppliers.contacts.contactName') }}
                </th-sortable>

                <th-sortable
                  :direction="
                    currentSortFieldForContacts === 'name'
                      ? currentSortDirectionForContacts
                      : 'none'
                  "
                  field-name="name"
                  @change="onContactsSortOrderChange"
                  >{{ $t('suppliers.contacts.supplierName') }}
                </th-sortable>

                <th-sortable
                  :direction="
                    currentSortFieldForContacts === 'email'
                      ? currentSortDirectionForContacts
                      : 'none'
                  "
                  field-name="email"
                  @change="onContactsSortOrderChange"
                  >{{ $t('suppliers.contacts.email') }}
                </th-sortable>
                <th-sortable
                  :direction="
                    currentSortFieldForContacts === 'phone1'
                      ? currentSortDirectionForContacts
                      : 'none'
                  "
                  field-name="phone1"
                  @change="onContactsSortOrderChange"
                  >{{ $t('suppliers.contacts.phone') }}
                </th-sortable>
              </template>
            </tol-table>
          </section>
          <section v-if="supplierContractors.length" class="details-section">
            <h1>{{ $t('suppliers.labels.contractors') }}</h1>
            <tol-table :table-data="supplierContractors" :columns="contractorsColumns">
              <template v-slot:tableHeader>
                <th>{{ $t('suppliers.contractors.referenceNumber') }}</th>
                <th-sortable
                  :direction="
                    currentSortFieldForContractors === 'payrollnums'
                      ? currentSortDirectionForContractors
                      : 'none'
                  "
                  field-name="payrollnums"
                  @change="onContractorSortOrderChange"
                  >{{ $t('suppliers.contractors.payroll_number') }}
                </th-sortable>
                <th-sortable
                  :direction="
                    currentSortFieldForContractors === 'contractorName'
                      ? currentSortDirectionForContractors
                      : 'none'
                  "
                  field-name="contractorName"
                  @change="onContractorSortOrderChange"
                  >{{ $t('suppliers.contractors.name') }}
                </th-sortable>

                <th-sortable
                  :direction="
                    currentSortFieldForContractors === 'entity'
                      ? currentSortDirectionForContractors
                      : 'none'
                  "
                  field-name="entity"
                  @change="onContractorSortOrderChange"
                  >{{ $t('suppliers.contractors.entity') }}
                </th-sortable>

                <th-sortable
                  :direction="
                    currentSortFieldForContractors === 'pay_to_name'
                      ? currentSortDirectionForContractors
                      : 'none'
                  "
                  field-name="pay_to_name"
                  @change="onContractorSortOrderChange"
                  >{{ $t('suppliers.contractors.pay_to_name') }}
                </th-sortable>

                <th-sortable
                  :direction="
                    currentSortFieldForContractors === 'email'
                      ? currentSortDirectionForContractors
                      : 'none'
                  "
                  field-name="email"
                  @change="onContractorSortOrderChange"
                  >{{ $t('suppliers.contractors.email') }}
                </th-sortable>
              </template>
            </tol-table>
          </section>
        </div>
      </div>
    </div>
    <ConfirmModal
      v-if="isDeleteAttachmentModalDisplayed"
      action="DELETE_ATTACHMENT"
      :title="$t('attachments.confirmDeleteTitle')"
      @continue-action="deleteAttachment()"
      @cancel-action="cancelDeletingAttachment()"
    >
      <template v-slot:modalMessage>
        {{ $t('attachments.confirmDelete') }}
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import { AppActions } from '@/app/app.keys';
import { attachmentService } from '@/app/components/attachments/attachment.service';
import ConfirmModal from '@/app/components/ConfirmModal';
import { SupplierDetailsActions, SupplierDetailsGetters } from './suppliers-details.keys';
import SupplierDetailsForm from './components/SupplierDetailsForm';
import TolTable from '@/app/components/TolTable';
import ThSortable from '@/app/components/ThSortable';
import Attachment from '@/app/components/attachments/Attachment';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';

export default {
  name: 'SuppliersDetails',
  components: {
    ConfirmModal,
    SupplierDetailsForm,
    TolTable,
    ThSortable,
    Attachment,
    AttachmentsList
  },

  data() {
    return {
      sidebarMenu: [
        {
          label: this.$t('suppliers.labels.details')
        },
        { label: this.$t('suppliers.labels.attachment') },
        { label: this.$t('suppliers.labels.history') },
        { label: this.$t('suppliers.labels.contacts') },
        { label: this.$t('suppliers.labels.contractors') }
      ],
      contractorsColumns: [
        'reference_number',
        'payrollnums',
        'contractorName',
        'entity',
        'pay_to_name',
        'email'
      ],
      supplierContactsColumns: ['reference_number', 'contactName', 'name', 'email', 'phone1'],
      attachmentOptions: [
        'Articles of Incorporation or Incorporation Papers',
        'Banking Information',
        'HST / GST / QST Form',
        'Certificate of Insurance',
        'WSIB Certificate',
        'Insurance Accord(US)',
        'Agreement',
        'Credit Check',
        'Other'
      ],
      isDeleteAttachmentModalDisplayed: false,
      selectedAttachmentId: null
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    supplier: {
      get() {
        return this.$store.getters[SupplierDetailsGetters.CurrentSupplier];
      }
    },
    currentSortFieldForContractors() {
      return this.$store.getters[SupplierDetailsGetters.SupplierContractorsSortOrder].sortBy;
    },
    currentSortDirectionForContractors() {
      return this.$store.getters[SupplierDetailsGetters.SupplierContractorsSortOrder].sortDirection;
    },
    currentSortFieldForContacts() {
      return this.$store.getters[SupplierDetailsGetters.SupplierContactsSortOrder].sortBy;
    },
    currentSortDirectionForContacts() {
      return this.$store.getters[SupplierDetailsGetters.SupplierContactsSortOrder].sortDirection;
    },
    supplierContractors() {
      return this.$store.getters[SupplierDetailsGetters.CurrentSupplierContractors];
    },
    supplierContacts() {
      return this.$store.getters[SupplierDetailsGetters.CurrentSupplierContacts];
    },
    supplierAttachments() {
      return this.$store.getters[SupplierDetailsGetters.GetSupplierAttachments];
    },
    canViewAttachment() {
      return this.currentUser.permissions['SUPPLIER']['VIEW_ATTACHMENT'];
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        this.$store.dispatch(SupplierDetailsActions.RetrieveSupplier, val.params.reference_number);
      }
    }
  },
  destroyed() {
    this.$store.dispatch(SupplierDetailsActions.ResetCurrentSupplier);
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('suppliers.details.title'));
    this.$store.dispatch(
      SupplierDetailsActions.RetrieveSupplier,
      this.$route.params.reference_number
    );
  },
  methods: {
    onContractorSortOrderChange(sortOrder) {
      this.$store.dispatch(SupplierDetailsActions.UpdateSupplierContractorSortOrder, sortOrder);
    },
    onContactsSortOrderChange(sortOrder) {
      this.$store.dispatch(SupplierDetailsActions.UpdateSupplierContactsSortOrder, sortOrder);
    },
    confirmAttachmentDelete(attachmentId) {
      this.selectedAttachmentId = attachmentId;
      this.isDeleteAttachmentModalDisplayed = true;
    },
    async deleteAttachment() {
      await attachmentService.deleteAttachment(this.selectedAttachmentId);
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
      this.refreshAttachments();
    },
    cancelDeletingAttachment() {
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
    },
    refreshAttachments() {
      this.$store.dispatch(
        SupplierDetailsActions.RetrieveSupplierAttachments,
        this.$route.params.reference_number
      );
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.details-wrapper {
  display: flex;
  flex-direction: column;

  background-color: $background-level-2;
  .details-header {
    display: flex;

    padding: $details-padding;
    border-bottom: 2px solid $row-border;
    justify-content: space-between;
  }

  .details-body {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-right: 4px solid rgba(255, 255, 255, 0);
    &.modified {
      border-right: 4px solid $modified-form;
    }

    .content-panel-wrapper {
      flex: 0 85%;
      margin: 0 auto;
      .content-panel {
        display: block;
        background-color: $white;
        padding: 60px;
      }
    }

    .details-section {
      padding-bottom: 25px;

      h1 {
        border-bottom: 1px solid $row-border;
      }
    }
  }
}
</style>
